/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useEffect, useState } from 'react'
import { X, ExternalLink, TrendingUp, Award, Users } from 'lucide-react'
import { fetchJudgeLeaderboard, mapArenaModelToOpenRouter, type JudgeLeaderboardData } from '../../lib/judge-arena'

interface JudgeLeaderboardModalProps {
  onClose: () => void
  onSelectModel?: (modelId: string) => void
}

export function JudgeLeaderboardModal({ onClose, onSelectModel }: JudgeLeaderboardModalProps) {
  const [data, setData] = useState<JudgeLeaderboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showPreliminary, setShowPreliminary] = useState(true)

  useEffect(() => {
    loadLeaderboard()
  }, [showPreliminary])

  const loadLeaderboard = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetchJudgeLeaderboard(showPreliminary)
      setData(result)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectModel = (arenaModel: string) => {
    const openRouterModel = mapArenaModelToOpenRouter(arenaModel)
    if (openRouterModel && onSelectModel) {
      onSelectModel(openRouterModel)
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]"
      onClick={onClose}
    >
      <div
        className="bg-background-secondary border border-border rounded-lg w-[700px] max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background-secondary border-b border-border p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Award className="w-5 h-5 text-accent" />
            <h2 className="text-lg font-title">Judge Arena Leaderboard</h2>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://huggingface.co/spaces/AtlaAI/judge-arena"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost px-3 py-1 text-xs flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              View on HuggingFace
            </a>
            <button className="btn-ghost p-1" onClick={onClose}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {/* Controls */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={showPreliminary}
                  onChange={(e) => setShowPreliminary(e.target.checked)}
                  className="rounded border-border bg-background text-accent focus:ring-accent"
                />
                <span>Show preliminary results</span>
              </label>
            </div>
            <button
              onClick={loadLeaderboard}
              disabled={loading}
              className="btn-secondary px-3 py-1 text-xs"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-3"></div>
                <p className="text-sm text-foreground-muted">Loading leaderboard...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-500/10 border border-red-500/20 rounded p-4 mb-4">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Leaderboard Table */}
          {data && !loading && (
            <div className="space-y-4">
              {/* Info Banner */}
              <div className="bg-background-tertiary border border-border rounded p-3">
                <p className="text-xs text-foreground-secondary">
                  <strong>Judge Arena</strong> ranks LLM judges based on their evaluation quality through pairwise comparisons.
                  Higher ELO scores indicate better judging performance.
                </p>
                {data.lastUpdated && (
                  <p className="text-xs text-foreground-muted mt-1">
                    Last updated: {new Date(data.lastUpdated).toLocaleString()}
                  </p>
                )}
              </div>

              {/* Table */}
              <div className="border border-border rounded overflow-hidden">
                <table className="w-full">
                  <thead className="bg-background-tertiary">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-condensed text-foreground-secondary">Rank</th>
                      <th className="text-left px-4 py-2 text-xs font-condensed text-foreground-secondary">Model</th>
                      <th className="text-left px-4 py-2 text-xs font-condensed text-foreground-secondary">Organization</th>
                      <th className="text-right px-4 py-2 text-xs font-condensed text-foreground-secondary">
                        <div className="flex items-center justify-end gap-1">
                          <TrendingUp className="w-3 h-3" />
                          ELO
                        </div>
                      </th>
                      <th className="text-right px-4 py-2 text-xs font-condensed text-foreground-secondary">
                        <div className="flex items-center justify-end gap-1">
                          <Users className="w-3 h-3" />
                          Votes
                        </div>
                      </th>
                      <th className="text-right px-4 py-2 text-xs font-condensed text-foreground-secondary">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.entries.map((entry, index) => {
                      const openRouterModel = mapArenaModelToOpenRouter(entry.model)
                      const isAvailable = !!openRouterModel

                      return (
                        <tr 
                          key={index}
                          className={`border-t border-border ${isAvailable ? 'hover:bg-background-hover' : ''}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {entry.rank <= 3 && (
                                <Award className={`w-4 h-4 ${
                                  entry.rank === 1 ? 'text-yellow-400' :
                                  entry.rank === 2 ? 'text-gray-400' :
                                  'text-orange-400'
                                }`} />
                              )}
                              <span className="text-sm font-mono">{entry.rank}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium">{entry.model}</div>
                            {!isAvailable && (
                              <div className="text-xs text-foreground-muted">Not available in HiveMind</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-foreground-secondary">{entry.organization || '—'}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-mono font-semibold text-accent">{entry.elo.toFixed(0)}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-mono text-foreground-secondary">{entry.votes.toLocaleString()}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isAvailable && onSelectModel ? (
                              <button
                                onClick={() => handleSelectModel(entry.model)}
                                className="btn-primary px-3 py-1 text-xs"
                              >
                                Use This
                              </button>
                            ) : (
                              <span className="text-xs text-foreground-muted">—</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Markdown Info */}
              {data.markdown && (
                <div className="bg-background-tertiary border border-border rounded p-3">
                  <p className="text-xs text-foreground-muted whitespace-pre-wrap">{data.markdown}</p>
                </div>
              )}

              {/* Footer Info */}
              <div className="text-xs text-foreground-muted text-center pt-2">
                <p>
                  Data from{' '}
                  <a
                    href="https://huggingface.co/spaces/AtlaAI/judge-arena"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    AtlaAI Judge Arena
                  </a>
                  {' '}on HuggingFace
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

